import { Box, Button, Card, Divider, Stack, Typography } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { AuthBundleAddAutocomplete } from "./auth-bundle-add-autocomplete"
import { AuthBundleChip } from "./auth-bundle-chip"
import { EmptyDemandMessage } from "./empty-demand-message"

export function AuthBundleDemands({
    demandItems,
    scheduleTypeItems,
    featureItems,
    demandOptions,
    scheduleTypeOptions,
    featureOptions,
    onAddDemand,
    onAddScheduleType,
    onAddFeature,
    onRemoveDemand,
    onRemoveScheduleType,
    onRemoveFeature,
    onImport,
}) {
    return (
        <Card sx={{ p: 2.5 }}>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">Demands in this bundle</Typography>
                    <Typography variant="caption" sx={countSx}>
                        {demandItems.length + scheduleTypeItems.length + featureItems.length}
                    </Typography>
                </Stack>

                <Typography variant="overline" color="text.disabled">
                    Permissions
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Repeat
                        list={demandItems}
                        item={<AuthBundleChip onRemove={onRemoveDemand} />}
                        keyFn="value"
                        fallback={<EmptyDemandMessage label="No permissions in this bundle yet." />}
                    />
                </Stack>
                <AuthBundleAddAutocomplete
                    label="Add permission demand"
                    placeholder="Search permissions"
                    options={demandOptions}
                    getOptionLabel={getDemandOptionLabel}
                    onAdd={onAddDemand}
                    data-cy="auth-bundle-add-demand"
                />

                <Divider />
                <Typography variant="overline" color="text.disabled">
                    Schedule type demands
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Repeat
                        list={scheduleTypeItems}
                        item={<AuthBundleChip onRemove={onRemoveScheduleType} />}
                        keyFn="value"
                        fallback={<EmptyDemandMessage label="No schedule type demands in this bundle yet." />}
                    />
                </Stack>
                <AuthBundleAddAutocomplete
                    label="Add schedule type"
                    placeholder="Search schedule types"
                    options={scheduleTypeOptions}
                    getOptionLabel={getScheduleTypeOptionLabel}
                    onAdd={onAddScheduleType}
                    data-cy="auth-bundle-add-schedule-type"
                />

                <Divider />
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="overline" color="text.disabled">
                        Feature flags
                    </Typography>
                    <Box flex={1} />
                    <Button
                        size="small"
                        startIcon={<Iconify icon="eva:upload-outline" />}
                        onClick={onImport}
                        data-cy="auth-bundle-import-pack-inline"
                    >
                        Import pack
                    </Button>
                </Stack>
                <If value={featureItems.length > 0}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Repeat
                            list={featureItems}
                            item={<AuthBundleChip onRemove={onRemoveFeature} />}
                            keyFn="value"
                        />
                    </Stack>
                    <Else>
                        <EmptyDemandMessage label="No feature flags in this bundle yet. Import a pack to add a set in one step." />
                    </Else>
                </If>
                <AuthBundleAddAutocomplete
                    label="Add feature flag"
                    placeholder="Search feature flags"
                    options={featureOptions}
                    getOptionLabel={getFeatureOptionLabel}
                    onAdd={onAddFeature}
                    data-cy="auth-bundle-add-feature"
                />
            </Stack>
        </Card>
    )
}

function getDemandOptionLabel(option) {
    return option?.name || ""
}

function getScheduleTypeOptionLabel(option) {
    return `${option?.name} [${option?.id}]`
}

function getFeatureOptionLabel(option) {
    return `${option?.name} [${option?.id}]`
}

const countSx = {
    bgcolor: "background.neutral",
    color: "text.secondary",
    borderRadius: 0.75,
    px: 1,
    py: 0.25,
    fontWeight: 700,
}
